import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import '../../assets/styles/infrastructure_leasing_modal.scss';
import {
  acceptInfrastructureListingDeal,
  dispatchInfrastructureNotificationsRefresh,
} from '../../services/infrastructureLeasingListing.service';

function str(v) {
  if (v == null) return '';
  return String(v).trim();
}

/**
 * Seller: record agreement terms and move deal to pending seller signature.
 */
export default function InfrastructureLeasingDealAcceptModal({
  open,
  onClose,
  listingId,
  enquiryId,
  listingTitle,
  buyerName,
  onSuccess,
}) {
  const [agreementStartDate, setAgreementStartDate] = useState('');
  const [agreementEndDate, setAgreementEndDate] = useState('');
  const [agreedAmount, setAgreedAmount] = useState('');
  const [additionalTerms, setAdditionalTerms] = useState('');
  const [paymentFrequency, setPaymentFrequency] = useState('monthly');
  const [securityDeposit, setSecurityDeposit] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [renewalTerms, setRenewalTerms] = useState('');
  const [legalClauses, setLegalClauses] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const suffix = `${listingId || 'x'}-${enquiryId || 'y'}`;

  useEffect(() => {
    if (open) {
      setAgreementStartDate('');
      setAgreementEndDate('');
      setAgreedAmount('');
      setAdditionalTerms('');
      setPaymentFrequency('monthly');
      setSecurityDeposit('');
      setPaymentTerms('');
      setRenewalTerms('');
      setLegalClauses('');
      setSubmitting(false);
    }
  }, [open, listingId, enquiryId]);

  if (!open) return null;

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const lid = Number(listingId);
    const eid = Number(enquiryId);
    if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
      toast.error('Missing listing or enquiry.');
      return;
    }
    const start = str(agreementStartDate);
    const end = str(agreementEndDate);
    const amt = str(agreedAmount);
    if (!start || !end) {
      toast.warning('Agreement start and end dates are required.');
      return;
    }
    if (start > end) {
      toast.warning('End date must be on or after start date.');
      return;
    }
    if (!amt) {
      toast.warning('Final price / negotiated amount is required.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await acceptInfrastructureListingDeal(lid, eid, {
        agreementStartDate: start,
        agreementEndDate: end,
        agreedAmount: amt,
        additionalTerms: str(additionalTerms) || null,
        paymentFrequency: str(paymentFrequency) || 'monthly',
        securityDeposit: str(securityDeposit) || null,
        paymentTerms: str(paymentTerms) || null,
        renewalTerms: str(renewalTerms) || null,
        legalClauses: str(legalClauses) || null,
      });
      if (res?.success) {
        toast.success(res.message || 'Agreement saved. Continue to sign.');
        dispatchInfrastructureNotificationsRefresh();
        onSuccess?.(res.data);
        onClose();
      } else {
        toast.error(res?.message || 'Could not save.');
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message;
      toast.error(msg || 'Could not accept deal.');
    } finally {
      setSubmitting(false);
    }
  };

  const modalUi = (
    <div className="ile-modal-root">
      <div className="ile-modal-overlay" onClick={() => !submitting && onClose()} role="presentation">
        <div
          className="ile-modal-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`ile-accept-title-${suffix}`}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="ile-modal-panel__header">
            <button
              type="button"
              className="ile-modal-panel__close"
              onClick={() => !submitting && onClose()}
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
            <h2 className="ile-modal-panel__title" id={`ile-accept-title-${suffix}`}>
              Accept deal
            </h2>
            <p className="ile-modal-panel__subtitle">
              {str(listingTitle) || 'Listing'} · {str(buyerName) || 'Buyer'}
            </p>
          </div>

          <form className="ile-modal-panel__form" onSubmit={handleSubmit} noValidate>
            <div className="ile-modal-panel__body">
              <div className="ile-modal-block">
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="ile-form-label" htmlFor={`ile-acc-start-${suffix}`}>
                      Agreement start date <span className="text-danger">*</span>
                    </label>
                    <input
                      id={`ile-acc-start-${suffix}`}
                      type="date"
                      className="input-pill"
                      value={agreementStartDate}
                      onChange={(e) => setAgreementStartDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="ile-form-label" htmlFor={`ile-acc-end-${suffix}`}>
                      Agreement end date <span className="text-danger">*</span>
                    </label>
                    <input
                      id={`ile-acc-end-${suffix}`}
                      type="date"
                      className="input-pill"
                      value={agreementEndDate}
                      onChange={(e) => setAgreementEndDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-12">
                    <label className="ile-form-label" htmlFor={`ile-acc-amt-${suffix}`}>
                      Final price / negotiated amount <span className="text-danger">*</span>
                    </label>
                    <input
                      id={`ile-acc-amt-${suffix}`}
                      type="text"
                      className="input-pill"
                      value={agreedAmount}
                      onChange={(e) => setAgreedAmount(e.target.value)}
                      placeholder="e.g. ₹1,25,000 / month"
                      required
                    />
                  </div>
                  <div className="col-12">
                    <label className="ile-form-label" htmlFor={`ile-acc-terms-${suffix}`}>
                      Additional terms &amp; conditions (optional)
                    </label>
                    <textarea
                      id={`ile-acc-terms-${suffix}`}
                      className="input-pill"
                      style={{ minHeight: 100, resize: 'vertical' }}
                      value={additionalTerms}
                      onChange={(e) => setAdditionalTerms(e.target.value)}
                      placeholder="Payment schedule, access hours, insurance, etc."
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="ile-form-label" htmlFor={`ile-acc-pf-${suffix}`}>
                      Payment frequency
                    </label>
                    <select
                      id={`ile-acc-pf-${suffix}`}
                      className="input-pill"
                      value={paymentFrequency}
                      onChange={(e) => setPaymentFrequency(e.target.value)}
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="ile-form-label" htmlFor={`ile-acc-sd-${suffix}`}>
                      Security deposit (optional)
                    </label>
                    <input
                      id={`ile-acc-sd-${suffix}`}
                      type="text"
                      className="input-pill"
                      value={securityDeposit}
                      onChange={(e) => setSecurityDeposit(e.target.value)}
                      placeholder="e.g. ₹50,000"
                    />
                  </div>
                  <div className="col-12">
                    <label className="ile-form-label" htmlFor={`ile-acc-pt-${suffix}`}>
                      Payment terms &amp; conditions (optional)
                    </label>
                    <textarea
                      id={`ile-acc-pt-${suffix}`}
                      className="input-pill"
                      style={{ minHeight: 72, resize: 'vertical' }}
                      value={paymentTerms}
                      onChange={(e) => setPaymentTerms(e.target.value)}
                      placeholder="Due dates, penalties, GST, etc."
                    />
                  </div>
                  <div className="col-12">
                    <label className="ile-form-label" htmlFor={`ile-acc-rt-${suffix}`}>
                      Renewal terms (optional)
                    </label>
                    <textarea
                      id={`ile-acc-rt-${suffix}`}
                      className="input-pill"
                      style={{ minHeight: 72, resize: 'vertical' }}
                      value={renewalTerms}
                      onChange={(e) => setRenewalTerms(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <label className="ile-form-label" htmlFor={`ile-acc-lc-${suffix}`}>
                      Legal clauses — standard template (optional)
                    </label>
                    <textarea
                      id={`ile-acc-lc-${suffix}`}
                      className="input-pill"
                      style={{ minHeight: 72, resize: 'vertical' }}
                      value={legalClauses}
                      onChange={(e) => setLegalClauses(e.target.value)}
                      placeholder="Arbitration, governing law, indemnity…"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="ile-modal-panel__footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="ile-modal-btn-primary" disabled={submitting}>
                {submitting ? 'Saving…' : 'Continue to agreement'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalUi, document.body);
  }
  return modalUi;
}
